import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, ShoppingBag } from "lucide-react";
import { fmtIDR, productImg, effectivePrice, hasDiscount as hasDisc, discountLabel, LOGO } from "@/lib/ellaApi";
import { useStore } from "@/lib/store";
import { toast } from "sonner";

export default function ProductCard({ product, layout = "grid" }) {
  const { addToCart, updateCartQty, cartItems } = useStore();
  const [busy, setBusy] = useState(false);

  const inCart = cartItems.find((i) => i.ProductID === product.ProductID);
  const cartQty = Number(inCart?.Qty || 0);

  const price = Number(product.Price || 0);
  const finalPrice = effectivePrice(product.Price, product.DiscountType, product.Discount);
  const discounted = hasDisc(product.DiscountType);
  const stockOut = Number(product.Stock || 0) <= 0;

  const handleAdd = async (e) => {
    e?.preventDefault(); e?.stopPropagation();
    if (stockOut) { toast.error("Stok habis"); return; }
    if (busy) return;
    setBusy(true);
    try {
      const r = await addToCart(product, 1);
      if (!r.ok) toast.error(r.message || "Gagal menambah ke keranjang");
      else toast.success("Ditambahkan ke keranjang");
    } catch {
      toast.error("Gagal menambah ke keranjang");
    } finally {
      setBusy(false);
    }
  };

  const handleQty = async (e, delta) => {
    e?.preventDefault(); e?.stopPropagation();
    if (busy) return;
    const newQty = cartQty + delta;
    if (newQty > Number(product.Stock || 0)) { toast.error("Melebihi stok"); return; }
    setBusy(true);
    try {
      const r = await updateCartQty(product.ProductID, Math.max(0, newQty));
      // the API can answer status:true with a stock-limit message ("Stock hanya tersedia N")
      if (r?.message) toast.warning(r.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Link
      to={`/product/${product.ProductID}`}
      className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow flex flex-col group relative"
      data-testid={`product-card-${product.ProductID}`}
    >
      <div className="relative aspect-square bg-gray-50 overflow-hidden">
        <img
          src={productImg(product.ImagePath)}
          alt={product.Product}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => { e.target.src = LOGO; e.target.classList.add("object-contain","p-6","opacity-40"); }}
        />
        {discounted && (
          <div className="absolute top-2 left-2 bg-[#D32F2F] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            {discountLabel(product.DiscountType, product.Discount, fmtIDR)}
          </div>
        )}
        {stockOut && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="text-white font-bold text-sm">STOK HABIS</span>
          </div>
        )}
      </div>

      <div className="p-2.5 flex-1 flex flex-col">
        <h3 className="text-sm font-medium text-gray-900 line-clamp-2 min-h-[2.5rem] leading-snug">{product.Product}</h3>
        <div className="mt-1.5">
          {discounted && (
            <p className="text-[11px] text-gray-400 line-through leading-none">{fmtIDR(price)}</p>
          )}
          <p className="text-base font-bold text-[#D32F2F] font-display leading-tight">{fmtIDR(finalPrice)}</p>
        </div>
        <p className="text-[10px] text-gray-400 mt-0.5">Stok: {product.Stock}</p>

        <div className="mt-2">
          {cartQty > 0 ? (
            <div className="flex items-center justify-between bg-red-50 rounded-full p-1">
              <button onClick={(e) => handleQty(e, -1)} disabled={busy} data-testid={`product-qty-decrease-${product.ProductID}`} className="w-7 h-7 rounded-full bg-white text-[#D32F2F] flex items-center justify-center active:scale-95">
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="text-sm font-bold text-[#D32F2F]" data-testid={`product-qty-${product.ProductID}`}>{cartQty}</span>
              <button onClick={(e) => handleQty(e, 1)} disabled={busy} data-testid={`product-qty-increase-${product.ProductID}`} className="w-7 h-7 rounded-full bg-[#D32F2F] text-white flex items-center justify-center active:scale-95">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleAdd}
              disabled={stockOut || busy}
              data-testid={`product-add-${product.ProductID}`}
              className="w-full h-9 rounded-full bg-[#D32F2F] hover:bg-[#B71C1C] disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold text-xs flex items-center justify-center gap-1.5 active:scale-95 transition"
            >
              <ShoppingBag className="w-3.5 h-3.5" /> Tambah
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}
